import React, { useState, useCallback, useEffect } from 'react';
import { RootState } from 'store/ducks';
import { useSelector, useDispatch } from 'react-redux';
import { Tooltip } from 'react-tooltip';

import { Column, Row } from '../Layout/Grid';
import Text from '../Text';
import Table from 'components/Table';
import {
  ordersItemsModelOpenProtocolDevolution,
  getBalance
} from 'components/Table/Orders/models';
import Validator from 'components/Validator';
import { Visibility } from 'components/Visibility';
import { TableFilterStateOrderItems } from 'components/Table/Orders/interface';

import { EnumOrderItemStatusType, IOrderItem } from 'models/Order';
import { IBatchRequestResponse } from 'models/Batch';
import { EnumCategories, EnumReason, TicketCategoryTypeNumber } from 'models/Ticket';

import OrderServices from 'services/order.service';
import TicketRule from 'services/Ticket/rule.service';
import { lockedProduct } from 'utils/OpenProtocol';

import logoOnlife from "img/logo-onlife.svg";

import { ContainerInfoTable, InputThemer } from './styles';
import { useLocation } from 'react-router-dom';
import { PhasesProtocols } from 'pages/Pipefy/enum';

/**
 * @main OpenProtocolOrderItems is the main exported component
 */
const OpenProtocolOrderItems: React.FC<any> = (props) => {

  const { protocolCategory, isEditing, ticketReason, returnType } = props;

  const dispatch = useDispatch();
  const location = useLocation();

  const phaseStatus = location?.state?.phaseStatus;
  const ticketDevolution = location?.state?.ticketDevolution;

  const user = useSelector((state: RootState) => state.user);
  const order = useSelector((state: RootState) => state.openProtocolData.order);

  const filter: TableFilterStateOrderItems = {
    orderItemStatusType: order?.protheusOrderSale != null ? EnumOrderItemStatusType.REALIZADO : EnumOrderItemStatusType.PENDENTE,
  };

  const pageTotal = 10;

  const singleCategoryProduct = protocolCategory === '7' ||
    protocolCategory === '8';

  const singleCategoryProductCondition = protocolCategory === '7' ||
    (protocolCategory === '8' && ticketReason === '47');

  const [orderItems, setOrderItems] = useState<IBatchRequestResponse<IOrderItem>>();
  const [tableItemOrderBy, setTableItemOrderBy] = useState('number asc');
  const [currentPageItem, setCurrentPageItem] = useState(0);
  const [qtdBalanceItems, setQtdBalanceItems] = useState(0);
  const [freezedOrder] = useState(() => Object.freeze(order));
  const [selectedItem, setSelectedItem] = useState<IOrderItem>();
  const [totalItems, setTotalItems] = useState<any[]>([]);
  const [isSelectedAllItemsCheckbox, setIsSelectedAllItemsCheckbox] = useState(false);

  const getOrdersItem = useCallback(() => {
    setOrderItems(undefined);

    new OrderServices()
      .getItems(currentPageItem, pageTotal, tableItemOrderBy, order?.id, filter)
      .then((result: IBatchRequestResponse<IOrderItem> | undefined) => {
        if (result) {
          const { error, data, totalPages, pageSize, total } = result;
          setOrderItems({ error, data, totalPages, pageSize, total });
        }
      });
  }, [currentPageItem, tableItemOrderBy]);

  useEffect(() => {
    if (!isEditing && singleCategoryProduct)
      resetSelectOrderItem();
  }, [ticketReason]);

  const resetSelectOrderItem = () => {
    setSelectedItem(undefined);
    setOrder({
      ...order,
      items: [],
    });
  };

  async function getTotalItems() {
    new OrderServices()
      .getItems(0, 9999, tableItemOrderBy, order?.id, filter)
      .then((result: IBatchRequestResponse<IOrderItem> | undefined) => {
        if (result) {
          const { data } = result;
          if (data) setTotalItems(data);
        }
      });
  }

  const onInputLooseFocus = (
    e: React.FocusEvent<HTMLInputElement>,
    item: any
  ) => {
    const newOrder: any = { ...order };

    const itemBalance = getBalance(item, freezedOrder);

    const value = Number(e.target.value);
    const itemIndex = order.items
      .map((data: IOrderItem) => data.number)
      .indexOf(item.number);

    if (
      value > itemBalance ||
      value % item.returnMultiplier !== 0 ||
      value <= 0
    ) {
      e.target.value = `${itemBalance}`;
      return;
    }
    if (itemIndex > -1) {
      newOrder.items[itemIndex].returnQuantity = value;

      setOrder(newOrder);
      e.target.value = `${value}`;
    }
  };

  function onInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.keyCode === 13) {
      return e.preventDefault();
    }

    return e.keyCode === 69 ? e.preventDefault() : true;
  }

  function checkOrUncheckAll() {
    if (!isSelectedAllItemsCheckbox) {
      setOrder({
        ...order,
        items: props.protocolCategory === EnumCategories.DEVOLUCAO ||
          props.protocolCategory === EnumCategories.RECLAMACAO_TECNICA ||
          props.protocolCategory === EnumCategories.OCORRENCIA_TRANSPORTADORA ||
          (props.protocolCategory === EnumCategories.LOGISTICA && props.ticketReason === EnumReason.FALTA_DE_VOLUME) ?
          quantityBalance(totalItems.filter((i) => getBalance(i, freezedOrder) > 0 &&
            !lockedProduct(i.locked, protocolCategory, returnType, user, i?.cancelationDate))) :
          quantityBalance(totalItems)
      });
    } else {
      setOrder({ ...order, items: [] });
    }
    setIsSelectedAllItemsCheckbox(!isSelectedAllItemsCheckbox);
  }

  const quantityBalance = (orderData: Array<IOrderItem>) => {
    return orderData.map((item: IOrderItem) => ({
      ...item,
      returnQuantity:
        props.protocolCategory === EnumCategories.DEVOLUCAO ||
          props.protocolCategory === EnumCategories.RECLAMACAO_TECNICA ||
          props.protocolCategory === EnumCategories.OCORRENCIA_TRANSPORTADORA ||
          (props.protocolCategory === EnumCategories.LOGISTICA && props.ticketReason === EnumReason.FALTA_DE_VOLUME) ?
          getBalance(item, freezedOrder) : 0
    }));
  };

  function setOrder(value: any) {
    dispatch({ type: 'SET_OPEN_PROTOCOL_ORDER', data: value });
  }

  function onSelectOrderItem(data: IOrderItem) {
    if (singleCategoryProductCondition) {
      setSelectedItem(data);
    }

    if (data) {
      const isSelected = !!order?.items.find(
        (item: any) => item.number === data.number
      );

      if (isSelected && !singleCategoryProductCondition) {
        setOrder({
          ...order,
          items: [
            ...order.items.filter((item: any) => item.number !== data.number),
          ],
        });
      } else if (!isSelected) {
        if (singleCategoryProductCondition) {
          setOrder({
            ...order,
            items: [{
              ...data,
              returnQuantity: Number(data.quantity - data.returnQuantity)
            }],
          });
        } else {
          setOrder({
            ...order,
            items: [
              ...order?.items,
              {
                ...data,
                returnQuantity:
                  TicketRule.isDevolution(protocolCategory)
                    ? Number(data.quantity)
                    : 0,
              },
            ],
          });
        }
      }
    }
  }

  useEffect(() => {
    getOrdersItem();
  }, [getOrdersItem]);

  useEffect(() => {
    getTotalItems();
  }, []);

  useEffect(() => {
    if (orderItems) {
      const balanceItems = orderItems.data?.reduce((total, item) => total + item.balance, 0);

      setQtdBalanceItems(Number(balanceItems));
    }
  }, [orderItems]);

  const reactTooltipId = `ReactTooltip${Math.random().toString().split('.')[1]}`;
  const msgBlockedProduct = 'Devoluções de produtos tipo vacina devem ser solicitadas para a área responsável';

  const categoriesDevolution = ['1', '7', 'RECLAMAÇÃO TÉCNICA', 'DEVOLUÇÃO'];

  const disabledCheckAll = () => {
    if (validateDisableInputs()) {
      return validateDisableInputsImperative() ? true : qtdBalanceItems === 0;
    }

    return false;
  };

  const disableEditProductSelected = (data: any) => {
    if (validateDisableInputs()) {
      return validateDisableInputsImperative() ? true : getBalance(data, freezedOrder) <= 0;
    }

    return false;
  };

  const warningProtocolCategory = () => {
    if (singleCategoryProductCondition)
      return `Para protocolos de 
        ${TicketCategoryTypeNumber[protocolCategory]?.toLocaleLowerCase()}
        apenas um item pode ser selecionado`;

    return '';
  };

  const handleCheckedOrder = (data: IOrderItem) => {
    if (!lockedProduct(data.locked, protocolCategory, returnType, user, data?.cancelationDate)) {
      return selectedItem ? data.number === selectedItem.number : undefined;
    }

    return false;
  };

  const handleDefaultCheckedOrder = (data: IOrderItem) => {
    if (!lockedProduct(data.locked, protocolCategory, returnType, user, data?.cancelationDate)) {
      return !!order.items?.find((item: any) => item.number === data.number);
    }

    return false;
  };

  const validateDisableInputs = () => {
    if (categoriesDevolution.includes(protocolCategory)) {
      return true;
    }

    return false;
  };

  const validateDisableInputsImperative = () => {
    if (['1', 'DEVOLUÇÃO'].includes(protocolCategory)
      && phaseStatus === PhasesProtocols.AVALIACAO_LOGISTICA_REVERSA
      && ticketDevolution?.statusSituationDevolution === 'REJEITADO') {
      return true;
    }

    return false;
  };

  const isGreenDelivery: boolean = (order?.carbonEmission && order?.carbonEmission.length > 0) ?
    order?.carbonEmission !== 'Emite' : false;

  return (
    <Column className="animated fadeIn px-md-2 px-3 bg-white">
      <InputThemer>
        <ContainerInfoTable>
          <Row className='justify-between flex-md-column align-center align-md-none'>
            <Row className="payment-breakpoint">
              <Text className="font-bold color-blue-gray" size={12}>
                {`Itens do pedido ( Filial : ${order?.subsidiary} )`}
              </Text>

              <Visibility visible={!!(order?.sellerName && user?.userType === "employee")}>
                <Text className="d-block font-bold color-blue-gray px-2 d-sm-none" size={12}> | </Text>
                <Text className="font-bold color-blue-gray" size={12}>
                  {order?.sellerName}
                </Text>
              </Visibility>
            </Row>

            <Visibility visible={isGreenDelivery}>
              <Row className='grid-gap-2'>
                <Text className="d-block color-blue-gray mt-2 mb-1 font-italic" size={12}>
                  Esse pedido foi entregue usando nossa frota de veículos elétricos
                </Text>
                <img src={logoOnlife} alt="Logo Onlife" />
              </Row>
            </Visibility>
          </Row>

          <Text className="font-bold color-blue-gray" size={12}>
            {warningProtocolCategory()}
          </Text>
        </ContainerInfoTable>

        <Row className="absolute z-1" style={{ top: 77, left: 13 }}>
          <input
            type="checkbox"
            onClick={checkOrUncheckAll}
            defaultChecked={isSelectedAllItemsCheckbox}
            hidden={singleCategoryProductCondition}
            disabled={disabledCheckAll()}
          />
        </Row>

        <Validator value={order.items.length > 0} name="Pedido" required>
          <Table
            trackingKey="Abertura de Protocolos#Pedidos"
            error={orderItems?.error}
            onReload={getOrdersItem}
            setTableOrder={setTableItemOrderBy}
            tableOrder={tableItemOrderBy}
            currentPage={currentPageItem}
            data={orderItems?.data}
            pageSize={pageTotal}
            onPageChange={setCurrentPageItem}
            totalPages={orderItems?.totalPages}
            model={[
              {
                label: '',
                Component: (data: any) => (
                  <>
                    <input
                      id={`item-${data.number}`}
                      className={lockedProduct(data.locked, protocolCategory, returnType, user, data?.cancelationDate) ?
                        'disabled' : ''
                      }
                      type="checkbox"
                      data-tooltip-content={lockedProduct(data.locked, protocolCategory, returnType, user) ?
                        msgBlockedProduct : undefined
                      }
                      data-tooltip-id={reactTooltipId}
                      data-tooltip-place={'top'}
                      onClick={() => !lockedProduct(data.locked, protocolCategory, returnType, user, data?.cancelationDate) ?
                        onSelectOrderItem(data) : null
                      }
                      disabled={disableEditProductSelected(data)}
                      checked={handleCheckedOrder(data)}
                      defaultChecked={handleDefaultCheckedOrder(data)}
                    />
                    {lockedProduct(data.locked, protocolCategory, returnType, user) &&
                      <Tooltip id={reactTooltipId} variant="light" />
                    }
                  </>),
                size: 40,
                maxSize: 40,
              },
              ...ordersItemsModelOpenProtocolDevolution({
                order,
                freezedOrder,
                onInputKeyDown,
                onInputLooseFocus,
                protocolCategory,
                ticketReason,
                selectedItem,
                isEditing
              }),
            ]}
            completeBlankSpaces={false}
            rowHeight={40}
            className="table-scroll w-100"
          />
        </Validator>
      </InputThemer>
    </Column>
  );
};

export default OpenProtocolOrderItems;
